import { useEffect, useRef } from "react";
import * as API from "../../helpers/api";
import config from "../../config";
import { useUser } from "../../helpers/userContext";
import { useSocket } from "../../helpers/socket";
import {
	ACTIVITY_IDLE_AFTER_MS,
	ACTIVITY_SYNC_KEY,
	localDayKey,
	readLastActivityAt,
	startOfLocalDay,
	writeLastActivityAt
} from "../../helpers/activitySession";
import { describeRoute, recordEvent } from "../../helpers/platformEvents";

export const ACTIVITY_STATE_EVENT = "apit:activity-state";
export const ACTIVITY_HISTORY_EVENT = "apit:activity-history";
const FLUSH_AFTER_SECONDS = 30;

const localizedName = (label) => ({
	en: label, ru: label, hy: label, tr: label, ar: label, zh: label,
	fa: label, he: label, fr: label, es: label, pt: label
});

const ActivityTracker = ({ OpenClose }) => {
	const { user } = useUser();
	const { myNotifications } = useSocket();
	const lastActivityAt = useRef(Date.now());
	const lastTickAt = useRef(Date.now());
	const lastActivityWrite = useRef(0);
	const pendingByDay = useRef(new Map());
	const totals = useRef({ activeTime: 0, inactiveTime: 0 });
	const currentDay = useRef(localDayKey());
	const flushing = useRef(false);
	const lastSemanticAction = useRef({ key: "", at: 0 });

	useEffect(() => {
		if (!user?._id) return undefined;
		const currentUserId = user._id;
		const today = localDayKey();
		const stored = user.timeLapse?.find((item) => localDayKey(item?.date) === today);
		totals.current = {
			activeTime: Number(stored?.activeTime) || 0,
			inactiveTime: Number(stored?.inactiveTime) || 0
		};
		currentDay.current = today;
		lastActivityAt.current = readLastActivityAt();
		lastTickAt.current = Date.now();

		const publish = (status, now = Date.now()) => window.dispatchEvent(new CustomEvent(
			ACTIVITY_STATE_EVENT,
			{
				detail: {
					...totals.current,
					status,
					lastActivityAt: lastActivityAt.current,
					currentInactiveTime: Math.max(
						0,
						(now - Math.max(
							lastActivityAt.current + ACTIVITY_IDLE_AFTER_MS,
							startOfLocalDay(now)
						)) / 1000
					)
				}
			}
		));

		const flush = async () => {
			if (flushing.current) return;
			const payloads = Array.from(pendingByDay.current.entries())
				.filter(([, value]) => value.activeTime || value.inactiveTime)
				.map(([date, value]) => ({ date, ...value }));
			if (!payloads.length) return;
			payloads.forEach(({ date }) => pendingByDay.current.delete(date));
			flushing.current = true;
			try {
				const results = await Promise.allSettled(payloads.map((payload) =>
					API.patch(`${config.api.hr.user}/${currentUserId}/activity`, payload)
				));
				results.forEach((result, index) => {
					if (result.status === "fulfilled") return;
					const { date, activeTime, inactiveTime } = payloads[index];
					const queued = pendingByDay.current.get(date) || { activeTime: 0, inactiveTime: 0 };
					queued.activeTime += activeTime;
					queued.inactiveTime += inactiveTime;
					pendingByDay.current.set(date, queued);
					console.warn("Activity tracker send failed", result.reason);
				});
			} catch (error) {
				console.warn("Activity tracker flush failed", error);
			} finally {
				flushing.current = false;
				if (pendingByDay.current.size > 1) flush();
			}
		};

		const recordSemanticAction = async (method, label) => {
			const key = `${method}:${label}:${window.location.pathname}`;
			const now = Date.now();
			if (lastSemanticAction.current.key === key && now - lastSemanticAction.current.at < 1500) return;
			lastSemanticAction.current = { key, at: now };
			try {
				const route = describeRoute(window.location.pathname);
				const normalizedMethod = String(method).toUpperCase();
				const type = normalizedMethod === "SAVE" ? "UPDATE" : normalizedMethod;
				const result = await recordEvent({
					type,
					category: ["DOWNLOAD", "EXPORT", "DELETE", "PRINT", "SHARE"].includes(type)
						? "audit"
						: "business",
					module: route.module,
					entity: route.entity,
					entityId: route.entityId,
					actionLabel: label,
					navigation: {
						pathname: window.location.pathname,
						search: window.location.search.replace(/^\?/, ""),
						labelPath: route.labelPath
					},
					metadata: { source: "semantic-ui-action", localizedName: localizedName(label) }
				});
				if (result?.history) window.dispatchEvent(new CustomEvent(ACTIVITY_HISTORY_EVENT, {
					detail: { history: result.history }
				}));
			} catch (error) {
				console.warn("Activity history send failed", error);
			}
		};

		const noteActivity = () => {
			const now = Date.now();
			lastActivityAt.current = now;
			if (now - lastActivityWrite.current >= 1000) {
				lastActivityWrite.current = now;
				writeLastActivityAt(now);
			}
			publish("active", now);
		};

		const handleStorage = (event) => {
			if (event.key !== ACTIVITY_SYNC_KEY || !event.newValue) return;
			const timestamp = Number(event.newValue);
			if (!Number.isFinite(timestamp)) return;
			lastActivityAt.current = Math.max(lastActivityAt.current, timestamp);
			publish("active");
		};

		const handleClick = (event) => {
			noteActivity();
			const element = event.target?.closest?.("button, a, [role='button']");
			if (!element) return;
			const text = String(element.getAttribute("aria-label") || element.textContent || "")
				.trim().replace(/\s+/g, " ");
			const lower = text.toLowerCase();
			const isDownload = element.hasAttribute("download") || lower.includes("download");
			if (/\bexport\b/.test(lower)) recordSemanticAction("EXPORT", text || "Exported data");
			else if (isDownload) recordSemanticAction("DOWNLOAD", text || "Downloaded a file");
			else if (/\bupload\b/.test(lower)) recordSemanticAction("UPLOAD", text || "Uploaded a file");
			else if (/\bdelete|remove\b/.test(lower)) recordSemanticAction("DELETE", text || "Deleted");
			else if (/\bprint\b/.test(lower)) recordSemanticAction("PRINT", text || "Printed");
			else if (/\bshare\b/.test(lower)) recordSemanticAction("SHARE", text || "Shared");
			else if (/^(save|saved|submit|create|update)\b/.test(lower)) recordSemanticAction("SAVE", text || "Saved");
			else if (/^(cancel|back)\b/.test(lower)) recordSemanticAction("SYSTEM", text || "Cancelled");
		};
		const handleSubmit = () => {
			noteActivity();
			recordSemanticAction("SAVE", "Saved");
		};

		const tick = () => {
			const now = Date.now();
			const todayKey = localDayKey(now);
			if (todayKey !== currentDay.current) {
				currentDay.current = todayKey;
				totals.current = { activeTime: 0, inactiveTime: 0 };
			}
			const intervalStartedAt = lastTickAt.current;
			lastTickAt.current = now;
			const activeUntil = lastActivityAt.current + ACTIVITY_IDLE_AFTER_MS;
			const activeSeconds = Math.max(0, Math.min(now, activeUntil) - intervalStartedAt) / 1000;
			const inactiveSeconds = Math.max(0, now - Math.max(intervalStartedAt, activeUntil)) / 1000;
			const queued = pendingByDay.current.get(todayKey) || { activeTime: 0, inactiveTime: 0 };
			queued.activeTime += activeSeconds;
			queued.inactiveTime += inactiveSeconds;
			pendingByDay.current.set(todayKey, queued);
			totals.current.activeTime += activeSeconds;
			totals.current.inactiveTime += inactiveSeconds;
			const status = now >= activeUntil ? "passive" : "active";
			publish(status, now);
			if (queued.activeTime + queued.inactiveTime >= FLUSH_AFTER_SECONDS) flush();

			if (now - lastActivityAt.current >= 12000 && myNotifications?.some(
				(notification) => !notification.read?.includes(currentUserId)
			)) OpenClose?.open?.();
		};

		const passiveEvents = ["mousemove", "mousedown", "keydown", "wheel", "scroll", "touchstart", "pointerdown"];
		passiveEvents.forEach((name) => window.addEventListener(name, noteActivity, {
			passive: true,
			capture: name === "scroll"
		}));
		document.addEventListener("click", handleClick, true);
		document.addEventListener("submit", handleSubmit, true);
		window.addEventListener("storage", handleStorage);
		const interval = window.setInterval(tick, 1000);
		publish(Date.now() >= lastActivityAt.current + ACTIVITY_IDLE_AFTER_MS ? "passive" : "active");

		return () => {
			window.clearInterval(interval);
			passiveEvents.forEach((name) => window.removeEventListener(name, noteActivity, {
				capture: name === "scroll"
			}));
			document.removeEventListener("click", handleClick, true);
			document.removeEventListener("submit", handleSubmit, true);
			window.removeEventListener("storage", handleStorage);
			flush();
		};
		// The tracker lifetime is tied to the authenticated user; rerunning it for
		// context object identity changes would discard unsent counters.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?._id]);

	return null;
};

export default ActivityTracker;
